import { useEffect, useRef, useState } from "react"
import {
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    CONSECUTIVE_SAMPLES_TO_CONFIRM,
    isFrameObstructed,
    luminanceStats,
} from "./cameraFrameAnalysis"
import { IMAGE } from "./deviceCheckRules"

/**
 * L'objectif de l'élève est-il couvert, avant même d'entrer en séance ?
 *
 * Même mesure que la détection d'obstruction de la caméra tuteur : on réutilise
 * `cameraFrameAnalysis`, donc les mêmes seuils et la même exigence de trois
 * relevés concordants. La cadence, elle, est resserrée — ici l'élève attend le
 * verdict, alors que la surveillance en séance peut prendre son temps.
 *
 * **On lit les images de l'aperçu affiché, et rien d'autre.** Une version
 * antérieure ouvrait un flux de secours quand l'aperçu tardait à répondre. Ce
 * secours n'a plus lieu d'être depuis que l'aperçu est monté par `CameraPreview`
 * : c'est nous qui ouvrons la caméra, donc si l'aperçu ne rend pas d'image,
 * c'est que la caméra ne s'ouvre pas — un second flux sur le même objectif
 * échouerait pareillement, tout en réintroduisant l'accès concurrent que ce
 * fichier existe pour éviter. Le délai de grâce qui le déclenchait était par
 * ailleurs devenu trop court : une première ouverture de caméra sous Windows
 * dépasse couramment la seconde et demie.
 *
 * Sans image lisible, on ne conclut donc rien — et on rend la main, pour ne pas
 * laisser l'étape caméra en attente indéfinie. Les deux issues se distinguent
 * dans `image` : `JUDGED` quand l'analyse a lu l'image et tranché, `GAVE_UP`
 * quand elle a renoncé. La différence compte pour l'appelant : une image lue
 * et non noire valide l'étape toute seule, une image qu'on n'a pas su lire
 * rend la question à l'élève.
 *
 * `image` est une chaîne, et c'est délibéré : React abandonne une mise à jour
 * dont l'état est identique, donc les relevés qui confirment le même verdict
 * toutes les 700 ms ne provoquent aucun rendu. Un objet — même de contenu
 * identique — échouerait cette comparaison et ferait rendre le panneau à
 * chaque relevé.
 *
 * Sans cette détection, l'écran affichait « on te voit bien » devant une image
 * noire.
 */

const SAMPLE_INTERVAL_MS = 700

/** Passé ce délai sans verdict, on rend la main sans rien affirmer. */
const GIVE_UP_MS = 4500

/** L'aperçu de la caméra est le `<video>` que le composant a monté dans ce conteneur. */
function findPreviewVideo(host) {
    if (!host) return null
    if (host.tagName === "VIDEO") return host
    return typeof host.querySelector === "function" ? host.querySelector("video") : null
}

const isUsable = (video) => !!video && video.readyState >= 2 && !video.paused

export const useCameraObstruction = ({ enabled, previewRef }) => {
    const [isObstructed, setIsObstructed] = useState(false)
    /*
     * Tant que l'analyse regarde, l'appelant doit rester en attente plutôt que
     * d'annoncer une caméra bonne qu'il devra démentir ensuite.
     *
     * `isObstructed` reste à part, et n'est pas replié dans cet état : une
     * caméra jugée noire dont on retire le cache doit repasser au vert, donc ce
     * fait-là doit pouvoir revenir en arrière, alors que « l'analyse a tranché »
     * ne revient jamais.
     */
    const [image, setImage] = useState(IMAGE.LOOKING)

    const canvasRef = useRef(null)
    const ctxRef = useRef(null)
    const bufferRef = useRef(null)
    const darkRef = useRef(0)
    const clearRef = useRef(0)

    useEffect(() => {
        if (!enabled || typeof document === "undefined") {
            setIsObstructed(false)
            setImage(IMAGE.LOOKING)
            darkRef.current = 0
            clearRef.current = 0
            return undefined
        }

        if (!canvasRef.current) {
            const canvas = document.createElement("canvas")
            canvas.width = CANVAS_WIDTH
            canvas.height = CANVAS_HEIGHT
            canvasRef.current = canvas
            ctxRef.current = canvas.getContext("2d", { willReadFrequently: true })
            bufferRef.current = new Float32Array(CANVAS_WIDTH * CANVAS_HEIGHT)
        }

        const ctx = ctxRef.current
        const buffer = bufferRef.current

        const sample = () => {
            const video = findPreviewVideo(previewRef?.current)
            if (!isUsable(video)) return

            try {
                ctx.drawImage(video, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
                const { data } = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

                if (isFrameObstructed(luminanceStats(data, buffer))) {
                    clearRef.current = 0
                    darkRef.current += 1
                    if (darkRef.current >= CONSECUTIVE_SAMPLES_TO_CONFIRM) {
                        setIsObstructed(true)
                        setImage(IMAGE.JUDGED)
                    }
                } else {
                    darkRef.current = 0
                    clearRef.current += 1
                    if (clearRef.current >= CONSECUTIVE_SAMPLES_TO_CONFIRM) {
                        setIsObstructed(false)
                        setImage(IMAGE.JUDGED)
                    }
                }
            } catch (error) {
                // Canvas indisponible : on ne conclut pas plutôt que de conclure à tort.
            }
        }

        // Trois relevés à 700 ms donnent un verdict en deux secondes, pour un
        // coût négligeable — l'image analysée fait 32 × 24 pixels.
        const first = setTimeout(sample, 350)
        const timer = setInterval(sample, SAMPLE_INTERVAL_MS)

        /*
         * Filet de sécurité : si l'analyse n'aboutit pas — aucune image lisible,
         * canvas indisponible — l'élève resterait bloqué sur « je regarde ton
         * image… ». Passé ce délai on rend la main sans rien affirmer sur
         * l'obstruction.
         *
         * Ce n'est pas un abandon : les relevés continuent, et une image qui
         * finit par arriver sera jugée. Une première ouverture de caméra sous
         * Windows dépasse couramment ce délai, et le verdict tardif vaut mieux
         * que pas de verdict. C'est pourquoi il ne faut PAS couper l'intervalle
         * ici, et pourquoi un verdict rendu ne doit pas écraser la réponse que
         * l'élève a donnée entre-temps (voir `cameraVerdict`).
         */
        const giveUp = setTimeout(
            () => setImage((current) => (current === IMAGE.LOOKING ? IMAGE.GAVE_UP : current)),
            GIVE_UP_MS
        )

        return () => {
            clearTimeout(first)
            clearTimeout(giveUp)
            clearInterval(timer)
        }
    }, [enabled, previewRef])

    return { isObstructed, image }
}
